import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useDemoSafeMutation } from "@/hooks/useDemoGuard";
import { useAuth } from "@/hooks/useAuth";
import { useEmployeeRecord } from "@/hooks/useEmployeeData";
import { useLanguage } from "@/contexts/LanguageContext";
import { employeesRepository } from "@/repositories";
import { toast } from "sonner";

export interface EmployeePropertyAssignment {
  id: string;
  company_id: string;
  employee_id: string;
  property_id: string;
  assigned_by: string | null;
  assigned_at: string;
}

/** List all property assignments for an employee */
export function useEmployeePropertyAssignments(employeeId: string | undefined) {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ["employee-property-assignments", employeeId, companyId],
    queryFn: async () => {
      if (!employeeId || !companyId) return [];
      return employeesRepository.listAssignments(employeeId, companyId);
    },
    enabled: !!employeeId && !!companyId,
  });
}

/** Assign properties to an employee (bulk) */
export function useAssignProperties() {
  const queryClient = useQueryClient();
  const { companyId, user } = useAuth();
  const { t } = useLanguage();

  return useDemoSafeMutation({
    mutationFn: async ({
      employeeId,
      propertyIds,
    }: {
      employeeId: string;
      propertyIds: string[];
    }) => {
      if (!companyId) throw new Error("No company ID");
      return employeesRepository.assignProperties(employeeId, companyId, propertyIds, user?.id ?? null);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["employee-property-assignments", variables.employeeId],
      });
      queryClient.invalidateQueries({ queryKey: ["properties"] });
      toast.success(t("تم إسناد العقارات بنجاح", "Properties assigned successfully"));
    },
    onError: () => {
      toast.error(t("حدث خطأ أثناء إسناد العقارات", "Error assigning properties"));
    },
  });
}

/** Unassign specific properties from an employee */
export function useUnassignProperties() {
  const queryClient = useQueryClient();
  const { companyId } = useAuth();
  const { t } = useLanguage();

  return useDemoSafeMutation({
    mutationFn: async ({
      employeeId,
      propertyIds,
    }: {
      employeeId: string;
      propertyIds: string[];
    }) => {
      if (!companyId) throw new Error("No company ID");
      await employeesRepository.unassignProperties(employeeId, companyId, propertyIds);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["employee-property-assignments", variables.employeeId],
      });
      queryClient.invalidateQueries({ queryKey: ["properties"] });
      toast.success(t("تم إزالة الإسناد بنجاح", "Properties unassigned successfully"));
    },
    onError: () => {
      toast.error(t("حدث خطأ أثناء إزالة الإسناد", "Error unassigning properties"));
    },
  });
}

/** Clear all property assignments for an employee (removes restrictions) */
export function useClearAssignments() {
  const queryClient = useQueryClient();
  const { companyId } = useAuth();
  const { t } = useLanguage();

  return useDemoSafeMutation({
    mutationFn: async (employeeId: string) => {
      if (!companyId) throw new Error("No company ID");
      await employeesRepository.clearAssignments(employeeId, companyId);
    },
    onSuccess: (_data, employeeId) => {
      queryClient.invalidateQueries({
        queryKey: ["employee-property-assignments", employeeId],
      });
      queryClient.invalidateQueries({ queryKey: ["properties"] });
      toast.success(
        t(
          "تم إزالة جميع الإسنادات — الموظف لن يرى أي عقارات حتى يتم إسنادها له",
          "All assignments cleared — employee will see no properties until reassigned"
        )
      );
    },
    onError: () => {
      toast.error(t("حدث خطأ أثناء إزالة الإسنادات", "Error clearing assignments"));
    },
  });
}

/**
 * Shared hook: returns assigned property IDs for current employee user.
 * Returns null for admin/manager (no filtering needed).
 * Returns empty array for employees with no assignments (sees nothing).
 * Returns string[] for employees with assignments (sees only those).
 */
export function useAssignedPropertyIds() {
  const { activeRole } = useAuth();
  const isEmployee = activeRole === "employee";
  const { data: employeeRecord, isLoading: employeeLoading } = useEmployeeRecord();
  const { data: assignments, isLoading: assignmentsLoading } = useEmployeePropertyAssignments(
    isEmployee ? employeeRecord?.id : undefined
  );

  if (!isEmployee) return { assignedPropertyIds: null, isLoading: false };
  if (employeeLoading || assignmentsLoading) return { assignedPropertyIds: null, isLoading: true };
  if (!assignments || assignments.length === 0) return { assignedPropertyIds: [], isLoading: false };
  return { assignedPropertyIds: assignments.map((a) => a.property_id), isLoading: false };
}
